import type { ChipDto, ChipMatch } from '@pietrochip/shared';
import type { ReactNode } from 'react';
import { personName, time } from '../lib/format';
import { StateMark } from './ui';

/**
 * I risultati della ricerca, mentre l'operatore digita.
 *
 * Due cose contano più di tutte: il codice si legge a colpo d'occhio (è quello
 * che l'operatore ha davanti sul transponder) e il pulsante per chiudere il
 * chip è grande abbastanza da essere premuto con un pollice, in piedi, con il
 * sole addosso. Il resto — nome, squadra, gara — serve a confermare che è
 * proprio lui.
 *
 * Con un risultato solo il riquadro si evidenzia: è il caso normale del lettore
 * barcode, e significa "premi Invio e va bene".
 */
export function SearchResults({
  matches,
  canAdd,
  addCode,
  busyChipId,
  onReturn,
  onUnreturn,
  onAddAndReturn,
  onAddOnly,
  onCorrectCode,
  onRemove,
}: {
  matches: readonly ChipMatch<ChipDto>[];
  /** Il codice cercato non esiste: si può aggiungere al volo. */
  canAdd: boolean;
  addCode: string;
  busyChipId: string | null;
  onReturn: (chip: ChipDto) => void;
  onUnreturn: (chip: ChipDto) => void;
  onAddAndReturn: (code: string) => void;
  onAddOnly: (code: string) => void;
  /** Riseleziona il testo nel campo, per correggere una lettura sbagliata. */
  onCorrectCode: () => void;
  onRemove: (chip: ChipDto) => void;
}) {
  if (matches.length === 0 && !canAdd) {
    return (
      <div className="results">
        <div className="result">
          <div className="result__main">
            <span className="result__code mono faint">Nessun chip trovato</span>
            <span className="result__who">
              Continua a digitare, oppure controlla di aver letto bene il codice.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="results">
      {matches.map((match, index) => (
        <ResultRow
          key={match.chip.id}
          chip={match.chip}
          ranges={match.ranges}
          selected={matches.length === 1}
          primary={index === 0 && match.score === 0}
          busy={busyChipId === match.chip.id}
          onReturn={() => onReturn(match.chip)}
          onUnreturn={() => onUnreturn(match.chip)}
          onRemove={() => onRemove(match.chip)}
        />
      ))}

      {canAdd && (
        /*
         * `role="alert"`: un codice che non è nell'elenco è l'unico caso in cui
         * chi usa un lettore di schermo deve essere interrotto. Invio non
         * aggiunge più niente da solo — la scelta è esplicita, perché un codice
         * letto male aggiunto in automatico sporca l'elenco proprio durante la
         * gara, e toglierlo dopo costa molto più che chiedere adesso.
         */
        <div className="result result--unknown" role="alert">
          <div className="result__main">
            <span className="result__code mono">
              <mark>{addCode}</mark>
            </span>
            <span className="result__who">
              Questo codice <strong>non è nell’elenco</strong> dell’evento. Se hai il transponder
              in mano puoi aggiungerlo; se il codice è sbagliato, correggilo qui sotto.
            </span>
          </div>
          <div className="result__side result__side--choices">
            <button
              type="button"
              className="btn btn--return btn--lg"
              disabled={busyChipId === addCode}
              onClick={() => onAddAndReturn(addCode)}
            >
              {busyChipId === addCode ? 'Aggiunta…' : 'Aggiungi e segna restituito'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busyChipId === addCode}
              onClick={() => onAddOnly(addCode)}
            >
              Aggiungi senza restituire
            </button>
            <button type="button" className="btn btn--quiet" onClick={onCorrectCode}>
              Correggi il codice
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultRow({
  chip,
  ranges,
  selected,
  primary,
  busy,
  onReturn,
  onUnreturn,
  onRemove,
}: {
  chip: ChipDto;
  ranges: readonly [number, number][];
  selected: boolean;
  primary: boolean;
  busy: boolean;
  onReturn: () => void;
  onUnreturn: () => void;
  onRemove: () => void;
}) {
  const returned = chip.returnedAt !== null;
  const who = personName(chip.firstName, chip.lastName);

  return (
    <div
      className={`result ${returned ? 'result--returned' : ''} ${selected ? 'result--selected' : ''}`}
    >
      <div className="result__main">
        <span className="result__code mono">
          <HighlightedCode code={chip.code} ranges={ranges} />
          {primary && <span className="faint"> · premi Invio</span>}
        </span>

        <span className="result__who">
          {who}
          {chip.team ? ` · ${chip.team}` : ''}
          {chip.race ? ` · ${chip.race}` : ''}
          {chip.raceNumber ? ` · n. ${chip.raceNumber}` : ''}
          {chip.addedManually && <span className="faint"> · aggiunto a mano</span>}
        </span>
      </div>

      <div className="result__side">
        {returned ? (
          <>
            <span className="row" style={{ gap: 8, fontSize: '0.875rem', color: 'var(--ink-soft)' }}>
              <StateMark returned withLabel={false} />
              <span>
                Restituito alle <span className="mono">{time(chip.returnedAt)}</span>
                {chip.returnedByName ? ` da ${chip.returnedByName}` : ''}
              </span>
            </span>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={onUnreturn}
              aria-label={`Annulla la restituzione di ${chip.code}`}
            >
              {busy ? '…' : 'Annulla'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn--return btn--lg"
            disabled={busy}
            onClick={onReturn}
            aria-label={`Segna restituito il chip ${chip.code}`}
          >
            {busy ? 'Registro…' : 'Restituito'}
          </button>
        )}

        <button
          type="button"
          className="btn btn--sm btn--quiet"
          disabled={busy}
          onClick={onRemove}
          aria-label={`Rimuovi il chip ${chip.code} dall’elenco`}
        >
          Rimuovi
        </button>
      </div>
    </div>
  );
}

/** Evidenzia la parte di codice che corrisponde a quello che si è digitato. */
function HighlightedCode({
  code,
  ranges,
}: {
  code: string;
  ranges: readonly [number, number][];
}): ReactNode {
  if (ranges.length === 0) return code;

  const parts: ReactNode[] = [];
  let cursor = 0;

  ranges.forEach((range, index) => {
    const [start, end] = range;
    if (start > cursor) parts.push(code.slice(cursor, start));
    parts.push(<mark key={index}>{code.slice(start, end)}</mark>);
    cursor = end;
  });

  if (cursor < code.length) parts.push(code.slice(cursor));
  return <>{parts}</>;
}
